/**
 * Seguimiento: las cinco secciones que solo INFORMAN, en una sola página con pestañas.
 *
 * Antes eran cinco entradas sueltas del menú —Transacciones, Cobros, Informes, Mantenimiento y
 * Portal del inquilino— al mismo nivel que Propiedades o Inquilinos, que es donde se trabaja.
 * Once entradas para elegir obligaban a leer el menú entero cada vez. Juntas, el menú queda en
 * seis y está claro qué es qué: ahí se gestiona; aquí se consulta lo que va pasando.
 *
 * Cada pestaña es la pantalla de siempre, sin tocar por dentro: `EmbeddedContext` hace que su
 * encabezado se repliegue (sin título, con su descripción y sus botones). La pestaña activa va
 * en la URL (`?tab=`), así que un enlace lleva directo a ella y el botón atrás funciona. Las
 * direcciones viejas (`/cobros`, `/transacciones`…) redirigen aquí: ningún marcador se rompe.
 */
import type { ReactNode } from 'react';
import { PageHeader } from '../components/PageHeader';
import { Tabs, type TabDefinition } from '../components/Tabs';
import { EmbeddedContext } from '../components/EmbeddedContext';
import { PendingPage } from './PendingPage';
import { RentCollectionPage } from './RentCollectionPage';
import { TransactionsPage } from './TransactionsPage';
import { MaintenancePage } from './MaintenancePage';
import { ReportsPage } from './ReportsPage';
import { PortalPage } from './PortalPage';

function Embedded({ children }: { children: ReactNode }) {
  return <EmbeddedContext.Provider value={true}>{children}</EmbeddedContext.Provider>;
}

/** Ids de pestaña, exportados para las redirecciones de las rutas antiguas. */
export const SEGUIMIENTO_TABS = {
  pendientes: 'pendientes',
  cobros: 'cobros',
  movimientos: 'movimientos',
  mantenimiento: 'mantenimiento',
  informes: 'informes',
  inquilino: 'inquilino',
} as const;

const TABS: TabDefinition[] = [
  // Primera: lo que hay que subir o hacer, por cuándo toca. La campana de arriba lleva aquí.
  {
    id: SEGUIMIENTO_TABS.pendientes,
    label: 'Pendientes',
    render: () => (
      <Embedded>
        <PendingPage />
      </Embedded>
    ),
  },
  // Quién ha pagado y quién no: lo que se mira cada mes.
  {
    id: SEGUIMIENTO_TABS.cobros,
    label: 'Cobros',
    render: () => (
      <Embedded>
        <RentCollectionPage />
      </Embedded>
    ),
  },
  {
    id: SEGUIMIENTO_TABS.movimientos,
    label: 'Movimientos del banco',
    render: () => (
      <Embedded>
        <TransactionsPage />
      </Embedded>
    ),
  },
  {
    id: SEGUIMIENTO_TABS.mantenimiento,
    label: 'Mantenimiento',
    render: () => (
      <Embedded>
        <MaintenancePage />
      </Embedded>
    ),
  },
  {
    id: SEGUIMIENTO_TABS.informes,
    label: 'Informes',
    render: () => (
      <Embedded>
        <ReportsPage />
      </Embedded>
    ),
  },
  {
    id: SEGUIMIENTO_TABS.inquilino,
    label: 'Vista del inquilino',
    render: () => (
      <Embedded>
        <PortalPage />
      </Embedded>
    ),
  },
];

export function SeguimientoPage() {
  return (
    <section>
      <PageHeader
        title="Seguimiento"
        subtitle="Pendientes, cobros, banco, mantenimiento e informes: lo que va pasando en tus inmuebles."
      />
      <Tabs tabs={TABS} ariaLabel="Secciones de seguimiento" />
    </section>
  );
}
